import { useEffect, useMemo } from "react";
import {
  AgGrid,
  Card,
  EmptyState,
  ErrorMessage,
  Flex,
  Grid,
  LoadingIndicator,
  Tag,
  Text,
} from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { ComplianceCaseTypeEnum as CaseType } from "~/graphql/supergraphEnums";
import { useGetEscalatedProfileRelationshipsQuery } from "~/graphql/supergraphHooks";
import {
  type ComplianceCaseTypeEnum,
  type GetComplianceCaseQuery,
} from "~/graphql/supergraphTypes";
import { isInternalEscalationSource } from "../../../utils/constants";
import { type EscalationPreviewRow, internalPreviewRow } from "./escalationPreview";
import { useExternalEscalationPreview } from "./useExternalEscalationPreview";

export type EscalationPreviewState = { count: number; loading: boolean };

type Props = {
  caseId: number;
  caseType: ComplianceCaseTypeEnum;
  complianceCase: NonNullable<GetComplianceCaseQuery["complianceCase"]>;
  escalatedProfileId: number | undefined;
  onPreviewChange?: (state: EscalationPreviewState) => void;
};

const SubjectCell = ({ label, showTag }: { label?: string; showTag?: boolean }) => (
  <Flex alignItems="center" gap={Sizes.SM}>
    <Text asSpan>{label}</Text>
    {showTag && <Tag variant="neutral" label="Originating case" />}
  </Flex>
);

const ClientCell = ({ data }: AgGrid.ICellRendererParams<EscalationPreviewRow>) =>
  data && data.position == null ? (
    <SubjectCell label={data.subject} showTag={data.isOriginatingCase} />
  ) : null;

const PositionCell = ({ data }: AgGrid.ICellRendererParams<EscalationPreviewRow>) =>
  data?.position ? (
    <SubjectCell label={data.position} showTag={data.isOriginatingCase} />
  ) : null;

const ROLE_AND_TYPE_COLUMNS: AgGrid.ColDef<EscalationPreviewRow>[] = [
  { field: "profileRole", headerName: "Profile role", flex: 1 },
  { field: "escalationType", headerName: "Escalation type", flex: 1 },
];

const EXTERNAL_COLUMN_DEFS: AgGrid.ColDef<EscalationPreviewRow>[] = [
  { field: "position", headerName: "Position", cellRenderer: PositionCell, flex: 2 },
  ...ROLE_AND_TYPE_COLUMNS,
];

const CLIENT_GROUP_COLUMN_DEF: AgGrid.ColDef<EscalationPreviewRow> = {
  headerName: "Client",
  flex: 2,
  cellRendererParams: { suppressCount: true, innerRenderer: ClientCell },
};

export const EscalationPreviewCard = ({
  caseId,
  caseType,
  complianceCase,
  escalatedProfileId,
  onPreviewChange,
}: Props) => {
  const isInternal = isInternalEscalationSource(caseType);

  const relationshipsQuery = useGetEscalatedProfileRelationshipsQuery({
    variables: { profileId: escalatedProfileId ?? 0 },
    skip: escalatedProfileId == null,
  });
  const relationships = relationshipsQuery.data?.complianceProfile?.parentRelationships;

  const external = useExternalEscalationPreview({
    caseId,
    caseType,
    escalatedProfileId,
    relationships,
    skip: isInternal,
  });

  const rows = useMemo<EscalationPreviewRow[]>(
    () =>
      isInternal
        ? [internalPreviewRow(complianceCase, escalatedProfileId, relationships)]
        : external.rows,
    [isInternal, complianceCase, escalatedProfileId, relationships, external.rows],
  );
  const loading = relationshipsQuery.loading || external.loading;
  const error = relationshipsQuery.error ?? external.error;
  const count = isInternal ? 1 : rows.filter((row) => row.path.length === 1).length;

  useEffect(() => {
    onPreviewChange?.({ count, loading });
  }, [count, loading, onPreviewChange]);

  const internalColumnDefs = useMemo<AgGrid.ColDef<EscalationPreviewRow>[]>(
    () => [
      {
        field: "subject",
        headerName: caseType === CaseType.EntityOnboarding ? "Entity" : "Client",
        flex: 2,
      },
      ...ROLE_AND_TYPE_COLUMNS,
    ],
    [caseType],
  );

  const renderBody = () => {
    if (error) {
      return <ErrorMessage>Could not load the impacted clients and positions.</ErrorMessage>;
    }
    if (loading) {
      return <LoadingIndicator />;
    }
    if (isInternal) {
      return (
        <Grid<EscalationPreviewRow>
          rowData={rows}
          columnDefs={internalColumnDefs}
          domLayout="autoHeight"
        />
      );
    }
    if (rows.length === 0) {
      return <EmptyState description="No positions were found for the selected profile." />;
    }
    return (
      <Grid<EscalationPreviewRow>
        rowData={rows}
        columnDefs={EXTERNAL_COLUMN_DEFS}
        treeData
        getDataPath={(row) => row.path}
        autoGroupColumnDef={CLIENT_GROUP_COLUMN_DEF}
        groupDefaultExpanded={-1}
        domLayout="autoHeight"
      />
    );
  };

  return (
    <Card data-testid="escalation-preview-card">
      <Flex flexDirection="column" gap={Sizes.MD}>
        <Text variant="strong-md">Escalations will be generated for the following:</Text>
        {renderBody()}
      </Flex>
    </Card>
  );
};
